import { ArrowRightIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { PIE_CHART_OTHERS_COLOR } from '@/pages/ui/pie-chart/constants'
import { PieChartContext } from '@/pages/ui/pie-chart/pie-chart-context'
import type { PieChartSliceData } from '@/pages/ui/pie-chart/types'

import { cn } from '@/lib/utils'

interface Props {
  data: PieChartSliceData[]
  dataOthers: PieChartSliceData[]
  formatter?: (value: number) => ReactNode
  labelFormatter?: (datum: PieChartSliceData) => ReactNode
  container?: React.RefObject<HTMLDivElement>
  onClick?: (datum: PieChartSliceData) => void
}

export function CaseTypeBreakdownTooltip({ data, dataOthers, formatter, labelFormatter, container, onClick }: Props) {
  const ctx = useContext(PieChartContext)
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  useLayoutEffect(() => {
    if (ref.current) {
      setSize({
        w: ref.current.offsetWidth,
        h: ref.current.offsetHeight,
      })
    }
  }, [ctx?.hovered])

  const hovered = ctx?.hovered ?? ctx?.tooltipHovered ?? null

  if (!ctx || !hovered || !container?.current) {
    return null
  }

  const { width: chartW, height: chartH, colors } = ctx
  const { index, x, y } = hovered

  const datum = data[index]
  const label = labelFormatter?.(datum) ?? datum.name
  const value = formatter?.(datum.value) ?? datum.value
  const color = colors[index % colors.length]

  const offset = 10
  let left = x + offset
  let top = y + offset

  // Flip horizontally if overflows right.
  if (left + size.w > chartW) {
    left = x - offset - size.w
  }
  // Flip vertically if overflows bottom.
  if (top + size.h > chartH) {
    top = y - offset - size.h
  }

  return createPortal(
    <div
      ref={ref}
      className={cn('absolute', 'whitespace-nowrap text-[14px] text-white', 'rounded-lg bg-slate-900', 'px-3 py-2')}
      style={{ left, top, transition: 'all 240ms ease-out' }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => ctx.setTooltipHovered(hovered)}
      onMouseLeave={() => ctx.setTooltipHovered(null)}
    >
      {datum.name.toLowerCase() === 'others' ? (
        <div className={cn('pt-1')}>
          <div className={cn('flex items-center justify-between', 'mb-2 mr-2 border-b border-slate-700 pb-1 pl-1')}>
            <div className={cn('flex items-center gap-1.5', 'text-white')}>
              <div className={cn('size-[12px] rounded-[6px]')} style={{ backgroundColor: PIE_CHART_OTHERS_COLOR }} />
              <div className={cn('text-[14px] font-[700]')}>Others</div>
            </div>
            <div className={cn('text-[14px] font-[300]')}>
              <div className={cn('text-[12px] font-[500] leading-[20px]')}>
                {(datum.ratio * 100).toFixed(2).replace(/\.?0+$/, '')}% ({value})
              </div>
            </div>
          </div>
          <div>
            {dataOthers.map((x) => {
              return (
                <div
                  key={x.name}
                  className={cn(
                    'flex items-center justify-between gap-2',
                    'ml-[10px] rounded-md px-2 py-1',
                    'text-[12px] font-[300] leading-[20px] text-white',
                    {
                      'hover:bg-slate-800': !!onClick,
                      'cursor-pointer': !!onClick,
                    }
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    onClick?.(x)
                  }}
                >
                  <div>{x.name}</div>
                  <div>
                    {(x.ratio * 100).toFixed(2).replace(/\.?0+$/, '')}% ({x.value})
                  </div>
                  <ArrowRightIcon className={cn('h-4 w-4 text-slate-300')} />
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className={cn('flex gap-1')}>
          <div className={cn('mt-[6px] h-[12px] w-[12px] rounded-[6px]')} style={{ backgroundColor: color }} />
          <div className={cn('flex flex-col', 'whitespace-nowrap text-[14px] text-white')}>
            <div
              className={cn('flex flex-1 items-center', 'rounded-md px-[7px] py-[2px]', {
                'hover:bg-slate-800': !!onClick,
                'cursor-pointer': !!onClick,
              })}
              onClick={(e) => {
                e.stopPropagation()
                onClick?.(datum)
              }}
            >
              <div className={cn('pr-4', 'font-[700]')}>{label}</div>
              <ArrowRightIcon className={cn('h-4 w-4 text-white')} />
            </div>
            <div className={cn('pl-[7px]', 'font-[300]')}>
              {(datum.ratio * 100).toFixed(2).replace(/\.?0+$/, '')}% ({value})
            </div>
          </div>
        </div>
      )}
      {/* <div>{value}</div> */}
    </div>,
    container.current
  )
}
CaseTypeBreakdownTooltip.displayName = 'Tooltip'
